import { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getAllSportList } from "../../../../services/commonApiService/CommonApiService";
import { getAllCategoryList, getEquipmentAllocationList } from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import ReactPaginate from "react-paginate";

const EquipmentAllocation = ({ setSelectedEquipments, initialEquipments, startDate, endDate }) => {

  const [qty, setQty] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const [category, setCategory] = useState([]);
  const [sports, setSports] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const [sportsLoading, setSportsLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(6);

  const [equipment, setEquipment] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [equipmentLoading, setEquipmentLoading] = useState(false);

  const [categorySearch, setCategorySearch] = useState("");
  const [sportSearch, setSportSearch] = useState("");
  const [open, setOpen] = useState({
    category: true,
    label: true,
    type: true,
    sport: true
  });

  const itemLabels = [
    { id: 'sport', name: "Sport" },
    { id: 'non-sport', name: "Non-Sport" }
  ];

  const itemTypes = [
    { id: 'consumable', name: "Consumable" },
    { id: 'non-consumable', name: "Non-Consumable" }
  ];

  const sectionRef = useRef(null);
  const prevScroll = useRef(0);

  // const increase = (id) => {

  //   prevScroll.current = window.scrollY;
  //   setQty((prev) => ({
  //     ...prev,
  //     [id]: (prev[id] || 0) + 1
  //   }));

  //   setTimeout(() => {
  //     window.scrollTo(0, prevScroll.current);
  //   }, 0);
  // };

 const increase = (item) => {

  const id = item?.inventory_item_id;
  const maxLimit = item?.allocated_quantity ?? 5;

  prevScroll.current = window.scrollY;

  setQty(prev => {
    const current = prev[id] || 0;
    return current >= maxLimit ? prev : { ...prev, [id]: current + 1 };
  });

  requestAnimationFrame(() => {
    window.scrollTo(0, prevScroll.current);
  });

};

  const decrease = (id) => {

    prevScroll.current = window.scrollY;
    setQty((prev) => ({
      ...prev,
      [id]: prev[id] > 0 ? prev[id] - 1 : 0
    }));

    setTimeout(() => {
      window.scrollTo(0, prevScroll.current);
    }, 0);
  };

  const toggleSection = () => {
    setIsOpen(prev => !prev);
  };

  useEffect(() => {

    const equipmentPayload = Object.entries(qty)
      .filter(([id, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({
        equipment_id: Number(id),
        quantity
      }));

    setSelectedEquipments(equipmentPayload);

  }, [qty]);

  useEffect(() => {
    if (initialEquipments?.length) {
      const map = {};

      initialEquipments.forEach((e) => {
        map[e.inventory_item_id] = e.quantity;
      });

      setQty(map);
    }
  }, [initialEquipments]);

  useEffect(() => {
    fetchCategory();
    fetchSportsList();
  }, []);

  // ================= SPORTS API =================

  const fetchSportsList = async () => {
    try {
      setSportsLoading(true);
      const response = await getAllSportList();
      setSports(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching sports list:", error);
    } finally {
      setSportsLoading(false);
    }
  };

  // ================= CATEGORY API =================

  const fetchCategory = async () => {
    try {
      setCategoryLoading(true);
      const response = await getAllCategoryList();
      setCategory(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching user types:", error);
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSport, selectedLabel, selectedType, currentPage, perPage, startDate, endDate]);

  /* fetch equipment list based on filters */
  const fetchEquipment = async () => {
    try {

      setEquipmentLoading(true);

      const payload = {
        per_page: perPage,
        page: currentPage,
        category_id: selectedCategory,
        sport_type: selectedSport,
        item_label: selectedLabel,
        item_type: selectedType,
        start_date: startDate,
        end_date: endDate,
      };

      const response = await getEquipmentAllocationList(payload);
      setEquipment(response?.data?.data || []);
      setTotalRecords(response?.data?.pagination?.total || 0);

    } catch (error) {
      console.error("Error fetching equipment list:", error);
    } finally {
      setEquipmentLoading(false);
    }
  }


  const toggle = (key) => {
    setOpen((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const totalSelectedEquipment = Object.values(qty).reduce(
    (sum, value) => sum + value,
    0
  );

  const filteredSports = sports?.filter(item =>
    item?.sport_name?.toLowerCase()?.includes(sportSearch?.toLowerCase())
  );
  const filteredCategory = category?.filter(item =>
    item?.category_name?.toLowerCase()?.includes(categorySearch?.toLowerCase())
  );

  const handlePageChange = (event) => {
    setCurrentPage(event.selected + 1);
  };

  return (
    <div className="card p-4 equipment-wrapper" ref={sectionRef}>

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">

        <h5 className="mb-0">Equipment Allocation</h5>

        <div className="d-flex align-items-center gap-3">

          <div
            style={{
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              padding: "6px 12px",
              fontSize: "14px",
              background: "#fff"
            }}
          >
            Equipment Selected: {totalSelectedEquipment}
            <span
              style={{
                marginLeft: "10px",
                color: "#7c3aed",
                cursor: "pointer",
                fontWeight: "500"
              }}
            >
              View
            </span>
          </div>

          <button
            className="btn"
            type="button"
            onClick={toggleSection}
          >
            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

        </div>

      </div>

      {isOpen && (
        <>
          {/* SEARCH */}
          <div className="equipment-search mb-4">
            <Search size={16} />
            <input placeholder="Search" />
          </div>

          <div className="row">

            {/* LEFT FILTER */}
            <div className="col-md-3">
              <div className="filter-card">

                <div className="d-flex justify-content-between mb-2">
                  <h6>Filters</h6>
                  <span
                    style={{ cursor: "pointer", color: "#7c3aed", fontSize: "13px" }}
                    onClick={() => {
                      setSelectedCategory(null);
                      setSelectedSport(null);
                      setSelectedLabel(null);
                      setSelectedType(null);
                    }}
                  >
                    Clear All
                  </span>
                </div>

                {/* CATEGORY */}
                <div className="filter-section">

                  <div
                    className="d-flex justify-content-between align-items-center filter-title"
                    onClick={() => toggle("category")}
                    style={{ cursor: "pointer" }}
                  >
                    <span>Category</span>
                    {open.category ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>

                  {open?.category && (
                    <>
                      <input
                        className="filter-search"
                        placeholder="Search..."
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                      />
                      <div className="flex flex-col overflow-auto" style={{ maxHeight: "20vh" }}>
                        {filteredCategory?.length === 0 ? (
                          <p className="text-center text-muted">No category found</p>
                        ) : (
                          filteredCategory?.map(item => (
                            <label
                              key={item?.id}
                              className="d-flex justify-content-between mb-2 span-text-all-filters"
                            >
                              <span>{item?.category_name}</span>
                              <input
                                type="radio"
                                checked={selectedCategory === item?.id}
                                onChange={() => setSelectedCategory(item?.id)}
                              />
                            </label>
                          ))
                        )}
                      </div>
                    </>
                  )}

                </div>


                {/* ITEM LABEL */}

                <div className="filter-section">

                  <div
                    className="d-flex justify-content-between align-items-center filter-title"
                    onClick={() => toggle("label")}
                    style={{ cursor: "pointer" }}
                  >
                    <span>Item Label</span>
                    {open.label ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>

                  {open.label && (
                    <>
                      {itemLabels?.map(item => (
                        <label key={item?.id} className="d-flex justify-content-between mb-2 span-text-all-filters">

                          <span>{item?.name}</span>

                          <input
                            type="radio"
                            checked={selectedLabel === item?.id}
                            onChange={() => setSelectedLabel(item?.id)}
                          />

                        </label>
                      ))}
                    </>
                  )}

                </div>


                {/* ITEM TYPE */}

                <div className="filter-section">

                  <div
                    className="d-flex justify-content-between align-items-center filter-title"
                    onClick={() => toggle("type")}
                    style={{ cursor: "pointer" }}
                  >
                    <span>Item Type</span>
                    {open.type ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>

                  {open.type && (
                    <>
                      {itemTypes?.map(item => (
                        <label key={item?.id} className="d-flex justify-content-between mb-2 span-text-all-filters">

                          <span>{item?.name}</span>

                          <input
                            type="radio"
                            checked={selectedType === item?.id}
                            onChange={() => setSelectedType(item?.id)}
                          />

                        </label>
                      ))}
                    </>
                  )}

                </div>


                {/* SPORT */}

                <div className="filter-section">

                  <div
                    className="d-flex justify-content-between align-items-center filter-title"
                    onClick={() => toggle("sport")}
                    style={{ cursor: "pointer" }}
                  >
                    <span>Sport</span>
                    {open?.sport ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>

                  {open?.sport && (
                    <>
                      <input
                        className="filter-search"
                        placeholder="Search sport..."
                        value={sportSearch}
                        onChange={(e) => setSportSearch(e.target.value)}
                      />
                      <div className="flex flex-col overflow-auto" style={{ maxHeight: "20vh" }}>

                        {filteredSports?.length === 0 ? (
                          <p className="text-center text-muted">No sport found</p>
                        ) : (
                          filteredSports?.map(item => (
                            <label
                              key={item?.sport_id}
                              className="d-flex justify-content-between mb-2 span-text-all-filters"
                            >
                              <span>{item?.sport_name}</span>

                              <input
                                type="radio"
                                checked={selectedSport === item?.sport_name}
                                onChange={() => setSelectedSport(item?.sport_name)}
                              />
                            </label>
                          ))
                        )}

                      </div>
                    </>
                  )}

                </div>
              </div>
            </div>

            {/* EQUIPMENT GRID */}
            <div className="col-md-9">

              <div className="row g-4">
                {equipment?.length === 0 && !equipmentLoading ? (
                  <p className="text-center text-muted equipment-record-found-margin">No equipment found</p>
                ) : (
                  equipment?.map((item) => (
                    <div className="col-md-4" key={item?.inventory_item_id}>

                      <div className="equipment-card">

                        <div className="equipment-image">
                          <img src={item?.image_url} alt={item?.name} />
                        </div>

                        <div className="equipment-info">
                          <span className="equipment-category">{item?.category_name}</span>

                          <h6 className="equipment-title">{item?.item_name}</h6>

                          <div className="equipment-bottom">

                            <div className="qty-control">

                              <button
                                type="button"
                                className="qty-btn"
                                onClick={() => decrease(item?.inventory_item_id)}
                              >
                                −
                              </button>

                              <span className="qty-value">
                                {qty[item?.inventory_item_id] || 0}
                              </span>

                              <button
                                type="button"
                                className="qty-btn"
                                disabled={(qty[item?.inventory_item_id] || 0) >= (item?.allocated_quantity ?? 5)}
                                onClick={() => increase(item)}
                              >
                                +
                              </button>

                            </div>

                            <span className="avg-text">Av. Qt. – {item?.allocated_quantity ?? 5}</span>

                          </div>

                        </div>

                      </div>
                    </div>
                  )))}

              </div>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <ReactPaginate
                  nextLabel="next >"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={5}
                  marginPagesDisplayed={2}
                  pageCount={Math.ceil(totalRecords / perPage)}
                  previousLabel="< previous"
                  containerClassName="pagination d-flex flex-wrap"
                  activeClassName="active"
                  pageClassName="page-item mx-1 my-1"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link mx-1 my-1"
                  nextClassName="page-item mx-1 my-1"
                  nextLinkClassName="page-link"
                  breakLabel="..."
                  breakClassName="page-item mx-1 my-1"
                  breakLinkClassName="page-link"
                  forcePage={currentPage - 1}
                />
              </div>

            </div>

          </div>
        </>
      )
      }

    </div >
  );
};

export default EquipmentAllocation;